import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { invitationsApi } from '@/api/invitationsWrapper';
import type { InvitationResponse } from 'we-tree-api-client/v2';
import { useFamilyStore } from '@/hooks/useStores';
import { uiStore } from '@/stores/UIStore';
import { getInvitationInviteeLabel } from '@/utils/invitationDisplay';
import EditInvitationModal from '@/components/invitations/EditInvitationModal';
import './invitations-list.css';

interface InvitationsListProps {
  familyId: string;
  onRefresh?: () => void;
}

export const InvitationsList: React.FC<InvitationsListProps> = observer(({ familyId, onRefresh }) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const familyStore = useFamilyStore();
  const [invitations, setInvitations] = useState<InvitationResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(null);
  const [editingInvitation, setEditingInvitation] = useState<InvitationResponse | null>(null);

  const loadInvitations = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await invitationsApi.getInvitations(familyId);
      if (result.success) {
        setInvitations(result.data.invitations);
      } else {
        // Handle permission errors gracefully
        if (result.error?.isAuthError() && result.error?.status === 403) {
          // Don't show error for permission issues - user just doesn't have access
          setError(null);
          setInvitations([]);
        } else {
          setError(result.error?.getUserMessage() || t('invitations.failedLoad'));
        }
      }
    } catch (err) {
      setError(t('invitations.failedLoad'));
      // eslint-disable-next-line no-console
      console.error('Load invitations error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [familyId, t]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const handleResend = async (invitationId: string) => {
    try {
      const result = await invitationsApi.resendInvitation(invitationId, {
        locale: uiStore.preferredLanguage
      });
      if (result.success) {
        // Update the invitation in the list with the new link if provided (dev mode)
        if (result.data.invitationLink) {
          setInvitations((prev) =>
            prev.map((inv) => (inv.id === invitationId ? { ...inv, invitationLink: result.data.invitationLink } : inv))
          );
        }
        // Refresh the list
        loadInvitations();
        if (onRefresh) {
          onRefresh();
        }
      } else {
        alert(result.error?.getUserMessage() || t('invitations.failedResend'));
      }
    } catch (err) {
      alert(t('invitations.failedResend'));
      // eslint-disable-next-line no-console
      console.error('Resend invitation error:', err);
    }
  };

  const handleCopyLink = async (invitationLink: string, invitationId: string) => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopiedInvitationId(invitationId);
      setTimeout(() => setCopiedInvitationId(null), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy to clipboard:', err);
      // Fallback: select the text
      const input = document.createElement('input');
      input.value = invitationLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedInvitationId(invitationId);
      setTimeout(() => setCopiedInvitationId(null), 2000);
    }
  };

  const handleCancel = async (invitationId: string) => {
    if (!confirm(t('confirm.cancelInvitation'))) {
      return;
    }

    try {
      const result = await invitationsApi.cancelInvitation(invitationId);
      if (result.success) {
        // Refresh the list
        loadInvitations();
        if (onRefresh) {
          onRefresh();
        }
      } else {
        alert(result.error?.getUserMessage() || t('invitations.failedCancel'));
      }
    } catch (err) {
      alert(t('invitations.failedCancel'));
      // eslint-disable-next-line no-console
      console.error('Cancel invitation error:', err);
    }
  };

  const handleDelete = async (invitationId: string) => {
    if (!confirm(t('confirm.deleteInvitation'))) {
      return;
    }

    try {
      const result = await invitationsApi.deleteInvitation(invitationId);
      if (result.success) {
        // Refresh the list
        loadInvitations();
        if (onRefresh) {
          onRefresh();
        }
      } else {
        alert(result.error?.getUserMessage() || t('invitations.failedDelete'));
      }
    } catch (err) {
      alert(t('invitations.failedDelete'));
      // eslint-disable-next-line no-console
      console.error('Delete invitation error:', err);
    }
  };

  const handleReinvite = async (invitationId: string) => {
    try {
      const result = await invitationsApi.resendInvitation(invitationId, {
        locale: uiStore.preferredLanguage
      });
      if (result.success) {
        // Update the invitation in the list with the new link if provided (dev mode)
        if (result.data.invitationLink) {
          setInvitations((prev) =>
            prev.map((inv) => (inv.id === invitationId ? { ...inv, invitationLink: result.data.invitationLink } : inv))
          );
        }
        // Refresh the list to get updated status
        loadInvitations();
        if (onRefresh) {
          onRefresh();
        }
      } else {
        alert(result.error?.getUserMessage() || t('invitations.failedReinvite'));
      }
    } catch (err) {
      alert(t('invitations.failedReinvite'));
      // eslint-disable-next-line no-console
      console.error('Reinvite error:', err);
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'invitations-list-status--pending';
      case 'accepted':
        return 'invitations-list-status--accepted';
      case 'expired':
        return 'invitations-list-status--expired';
      case 'cancelled':
        return 'invitations-list-status--cancelled';
      default:
        return 'invitations-list-status--expired';
    }
  };

  const formatStatusLabel = (status: string) => {
    const key = `invitations.status.${status}` as const;
    const translated = t(key);
    return translated === key ? status.toUpperCase() : translated;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isExpired = (invitation: InvitationResponse) => {
    if (invitation.status === 'expired') return true;
    if (invitation.status !== 'pending') return false;
    return new Date(invitation.expiresAt) < new Date();
  };

  if (isLoading) {
    return <div className="invitations-list-loading">{t('invitations.loading')}</div>;
  }

  if (error) {
    return <div className="invitations-list-error">{error}</div>;
  }

  // Filter invitations - show pending, expired, and cancelled
  const visibleInvitations = invitations.filter(
    (inv) => inv.status === 'pending' || inv.status === 'cancelled' || inv.status === 'expired' || isExpired(inv)
  );

  if (visibleInvitations.length === 0) {
    return <div className="invitations-list-empty">{t('invitations.empty')}</div>;
  }

  return (
    <div className="invitations-list">
      <h3 className="invitations-list-title">{t('invitations.title')}</h3>
      <table className="invitations-list-table">
        <thead>
          <tr>
            <th>{t('invitations.columnMember')}</th>
            <th>{t('invitations.columnStatus')}</th>
            <th>{t('invitations.columnExpires')}</th>
            <th>{t('invitations.columnActions')}</th>
          </tr>
        </thead>
        <tbody>
          {visibleInvitations.map((invitation) => {
            const expired = isExpired(invitation);
            const displayStatus = expired ? 'expired' : invitation.status;
            const isPending = displayStatus === 'pending' && !expired;
            const isCancelled = invitation.status === 'cancelled';
            const canReinvite = isCancelled || expired;
            const inviteeLabel = getInvitationInviteeLabel(invitation, (id) => familyStore.getPerson(id)?.fullName);

            return (
              <tr key={invitation.id}>
                <td>
                  <div className="invitations-list-name">{inviteeLabel}</div>
                </td>
                <td>
                  <span className={`invitations-list-status ${getStatusClass(displayStatus)}`}>
                    {formatStatusLabel(displayStatus)}
                  </span>
                </td>
                <td>{formatDate(invitation.expiresAt)}</td>
                <td>
                  {isPending && (
                    <div className="invitations-list-actions">
                      {invitation.invitationLink && (
                        <button
                          onClick={() => handleCopyLink(invitation.invitationLink!, invitation.id)}
                          className="invitations-list-button invitations-list-button--copy"
                          title={t('invitations.copyLinkTitle')}
                        >
                          {copiedInvitationId === invitation.id
                            ? t('invitations.copiedBadge', { label: tCommon('copied') })
                            : t('actions.copyLink')}
                        </button>
                      )}
                      <button onClick={() => setEditingInvitation(invitation)} className="invitations-list-button">
                        {tCommon('edit')}
                      </button>
                      <button
                        onClick={() => handleResend(invitation.id)}
                        className="invitations-list-button invitations-list-button--resend"
                      >
                        {t('actions.resend')}
                      </button>
                      <button
                        onClick={() => handleCancel(invitation.id)}
                        className="invitations-list-button invitations-list-button--cancel"
                      >
                        {tCommon('cancel')}
                      </button>
                    </div>
                  )}
                  {canReinvite && (
                    <div className="invitations-list-actions">
                      <button
                        onClick={() => handleReinvite(invitation.id)}
                        className="invitations-list-button invitations-list-button--resend"
                      >
                        {t('actions.reinvite')}
                      </button>
                      <button
                        onClick={() => handleDelete(invitation.id)}
                        className="invitations-list-button invitations-list-button--delete"
                      >
                        {tCommon('delete')}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <EditInvitationModal
        familyId={familyId}
        invitation={editingInvitation}
        isOpen={editingInvitation !== null}
        onClose={() => setEditingInvitation(null)}
        onSuccess={() => {
          // Refresh in place — do not call onRefresh (that remounts this list and would dismiss the modal)
          loadInvitations();
        }}
      />
    </div>
  );
});

export default InvitationsList;
